import {ContentAssessor, SEOAssessor} from '@axyseo/scoring/assessors/index';
import {groupAssessmentsByCategory, groupAssessmentsByStatus} from '../helpers';
import {idPointsMap} from '@axyseo/const/analysis';

/**
 * The AvadaAssessor class is used for the general SEO analysis.
 */
export default class AvadaAssessor {
  /**
   * Creates a new AvadaAssessor instance.
   * @param {Researcher}  researcher  The researcher to use.
   * @param {Object}    [config]  The assessor config.
   * @param {Object}  [config.textParagraphTooLong]
   * @param {Object}  [config.textSentenceLength]
   * @param {Object}  [config.textTransitionWords]
   * @param {Object}  [config.sentenceBeginnings]
   * @param {Object}  [config.relatedKeywords]
   * @param {Object}  [config.titleKeyword]
   * @param {Object}  [config.textImages]
   * @param {Object}  [config.numberTitle]
   * @param {Object}  [config.introductionKeyword]
   * @param {Object}  [config.keyphraseLength]
   * @param {Object}  [config.metaDescriptionKeyword]
   * @param {Object}  [config.metaDescriptionLength]
   * @param {Object}  [config.subheadingsKeyword]
   * @param {Object}  [config.externalLinks]
   * @param {Object}  [config.keyphraseDensity]
   * @param {Object}  [config.media]
   * @param {Object}  [config.urlLength]
   * @param {Object}  [config.textLength]
   * @param {Object}  [config.singleTitle]
   * @param {Object}  [config.keyphrase]
   * @param {Object}  [config.internalLinks]
   * @param {Object}  [config.titleWidth]
   * @param {Object}  [config.slugKeyword]
   * @param {Object}  [config.singleH1]
   * @param {Object}  [config.schema]
   */
  constructor(researcher, config = {}) {
    const seoAssessor = new SEOAssessor(researcher, config);
    const contentAssessor = new ContentAssessor(researcher, config);
    this.seoAssessor = seoAssessor;
    this.contentAssessor = contentAssessor;
    this.researcher = researcher;
    this.config = config;
  }

  assess(paper, i18n = null) {
    this.seoAssessor.assess(paper, i18n);
    this.contentAssessor.assess(paper, i18n);
    let assessments = [...this.seoAssessor.results, ...this.contentAssessor.results];
    assessments = assessments.map(assessment => ({
      ...assessment.config,
      status: assessment.status,
      score: assessment.score,
      data: assessment.data,
      title: assessment.title || assessment.config.title
    }));
    this.assessments = assessments;
  }

  assessBackEnd(paper) {
    this.seoAssessor.assess(paper);
    this.contentAssessor.assess(paper);
    let assessments = [...this.seoAssessor.results, ...this.contentAssessor.results];
    assessments = assessments.map(assessment => ({
      ...assessment.config,
      status: assessment.status,
      score: assessment.score,
      data: assessment.data,
      title: assessment.title || assessment.config.title
    }));
    this.assessments = assessments;
  }

  getResult(groupBy = 'category', getScore = true) {
    let assessments = [];
    switch (groupBy) {
      case 'category':
        assessments = groupAssessmentsByCategory(this.assessments);
        break;
      case 'status':
        assessments = groupAssessmentsByStatus(this.assessments);
        break;
    }

    return {
      assessments,
      ...(getScore ? {score: this.getScore()} : {})
    };
  }

  getScore() {
    let point = 0;
    let maxPoint = 0;
    this.assessments.forEach(assessment => {
      const weight = idPointsMap[assessment.id];
      if (weight === undefined) return;
      maxPoint += weight;
      point += assessment.score || 0;
    });
    if (!maxPoint) return 0;
    return Math.round(point / maxPoint * 100);
  }
}
